import { cn } from '@newfront-insurance/core-ui/v2';
import { useEffect, useRef } from 'react';

import { useAttachments } from './attachments-provider';
import { ChatAttachmentActions } from './chat-attachment-actions';
import { useDragHandlers } from './chat-input-context';
import { useChatInputContext } from './chat-input-context';
import { useFileSelection, type FileAttachment } from '../../../../hooks/use-file-selection';
import { readyUploads } from '../../../../lib/chat-workspace';
import type { UIMessageFileMetadata } from '../../../../lib/types';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatAttachmentsProps {
  className?: string;
  syncAdditionalContext?: boolean;
  /** The chat the files upload into, when not the one on screen (a composer for a chat not started yet). */
  chatUuid?: string;
}

/** The message metadata the chat renders for one uploaded attachment. */
export function buildMessageFileMetadata(attachment: FileAttachment): UIMessageFileMetadata {
  if (attachment.kind === 'folder' && attachment.folderMetadata) {
    return {
      path: attachment.upload!.path,
      filename: attachment.file.name,
      kind: 'folder',
      folderName: attachment.folderMetadata.folderName,
      fileCount: attachment.folderMetadata.fileCount,
      totalBytes: attachment.folderMetadata.totalBytes,
      skippedFileCount: attachment.folderMetadata.skippedFileCount,
      archiveFilename: attachment.folderMetadata.archiveFilename,
      truncated: attachment.folderMetadata.truncated,
    };
  }

  return {
    path: attachment.upload!.path,
    filename: attachment.upload!.name,
  };
}

/**
 * Component for managing file attachments in chat input.
 * Manages its own attachment state and file selection logic.
 * Contributes attachment data to additionalContext via ChatOverlayContext.
 *
 * This component is decoupled from the input setup - it simply sets additionalContext
 * which is then used when the message is sent.
 *
 * Includes only the attachment button and file input.
 * Note: The attachments preview is rendered separately.
 */
export function ChatAttachments({
  className,
  syncAdditionalContext = true,
  chatUuid: uploadChatUuid,
}: ChatAttachmentsProps) {
  // Manage attachments state locally
  const { attachments, setAttachments } = useAttachments();
  const { chatUuid, setAdditionalContext } = useChatOverlayContext();
  const { setHandlers } = useDragHandlers();
  const { status } = useChatInputContext();
  // Use file selection hook with local state
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    handleFolderAttachmentClick,
    supportedFileTypes,
  } = useFileSelection({ setAttachments, chatUuid: uploadChatUuid ?? chatUuid });

  // Track previous status to detect when message is sent
  const prevStatusRef = useRef(status);

  // Clear attachments when message is sent
  useEffect(() => {
    if (!syncAdditionalContext) {
      prevStatusRef.current = status;
      return;
    }

    if (prevStatusRef.current !== 'submitted' && status === 'submitted') {
      // Message was just sent, clear attachments
      setAttachments([]);
      setAdditionalContext((prev) => {
        const { attachmentUploads, messageMetadata, ...rest } = prev;
        return rest;
      });
    }
    prevStatusRef.current = status;
  }, [status, setAttachments, setAdditionalContext, syncAdditionalContext]);

  // Update drag handlers ref whenever they change
  useEffect(() => {
    setHandlers({
      isDragging,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handlePaste,
    });
  }, [isDragging, handleDragOver, handleDragLeave, handleDrop, handlePaste, setHandlers]);

  // Update additionalContext with attachment data whenever it changes
  useEffect(() => {
    if (!syncAdditionalContext) {
      return;
    }

    const successfulAttachments = attachments.filter((attachment) => attachment.upload && !attachment.uploadError);
    const attachmentUploads = readyUploads(successfulAttachments);
    const fileMetadata = successfulAttachments.map(buildMessageFileMetadata);

    setAdditionalContext((prev) => ({
      ...prev,
      attachmentUploads,
      // Set messageMetadata that will be attached to the message when sent
      // Components can contribute metadata by setting messageMetadata in additionalContext
      messageMetadata: fileMetadata.length > 0 ? { files: fileMetadata } : undefined,
    }));
  }, [attachments, setAdditionalContext, syncAdditionalContext]);

  return (
    <ChatAttachmentActions
      className={cn('flex items-center', className)}
      fileInputRef={fileInputRef}
      supportedFileTypes={supportedFileTypes}
      onAttachmentClick={handleAttachmentClick}
      onFileUpload={handleFileUpload}
      onFolderAttachmentClick={() => void handleFolderAttachmentClick()}
    />
  );
}
